import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook לוודא שכפתורים תמיד מגיבים ללחיצות
 * מוסיף מנגנון בדיקה שמוודא שהלחיצות עובדות
 */
export const useButtonHealthCheck = () => {
  const healthCheckIntervalRef = useRef(null);
  const lastInteractionRef = useRef(Date.now());

  // פונקציה לבדיקת בריאות הכפתורים
  const checkButtonHealth = useCallback(() => {
    const buttons = document.querySelectorAll('button, a[role="button"]');
    let healthyButtons = 0;
    let totalButtons = buttons.length;

    buttons.forEach((button, index) => {
      try {
        // בדיקה שהכפתור נראה ופעיל
        if (button.offsetWidth > 0 && button.offsetHeight > 0 && !button.disabled) {
          healthyButtons++;
          
          // וידוא שיש event listeners
          if (!button.dataset.healthChecked) {
            // הוספת backup event listener אם אין
            const originalOnClick = button.onclick;
            button.dataset.healthChecked = 'true';
            
            button.addEventListener('click', function(e) {
              lastInteractionRef.current = Date.now();
              
              // אם הlחיצה הראשית לא עובדת, ננסה חלופות
              setTimeout(() => {
                const timeSinceLastInteraction = Date.now() - lastInteractionRef.current;
                if (timeSinceLastInteraction > 500) {
                  console.warn(`🔧 Button health check: retrying click for button ${index}`);
                  
                  // ניסיון חלופי
                  if (originalOnClick && typeof originalOnClick === 'function') {
                    try {
                      originalOnClick.call(button, e);
                    } catch (error) {
                      console.error('Backup click handler failed:', error);
                    }
                  }
                }
              }, 100);
            }, { passive: false, capture: true });
          }
        }
      } catch (error) {
        console.error(`Error checking button ${index}:`, error);
      }
    });

    // דיווח על בריאות הכפתורים
    if (healthyButtons < totalButtons) {
      console.warn(`⚠️ Button health: ${healthyButtons}/${totalButtons} buttons are healthy`);
    }

    return { healthy: healthyButtons, total: totalButtons };
  }, []);

  // פונקציה לתיקון כפתורים שלא עובדים
  const repairButtons = useCallback(() => {
    const problematicButtons = document.querySelectorAll('button:not([data-health-checked]), a[role="button"]:not([data-health-checked])');
    
    problematicButtons.forEach((button) => {
      try {
        // הוספת fallback event listeners
        button.addEventListener('click', function(e) {
          lastInteractionRef.current = Date.now();
          
          // אם זה קישור, נוודא שהניווט עובד
          if (button.tagName === 'A' && button.href) {
            setTimeout(() => {
              if (window.location.href === button.href) return;
              
              console.warn('🔧 Navigation fallback triggered');
              window.location.href = button.href;
            }, 1000);
          }
        }, { passive: false });
        
        button.dataset.healthChecked = 'true';
      } catch (error) {
        console.error('Error repairing button:', error);
      }
    });
  }, []);

  // פונקציה להוספת global fallback לכל הלחיצות
  const addGlobalClickFallback = useCallback(() => {
    let isGlobalFallbackActive = false;
    
    // מאזין גלובלי שתופס לחיצות שלא עובדות
    document.addEventListener('click', function(e) {
      lastInteractionRef.current = Date.now();
      
      // בדיקה שהלחיצה אכן עשתה משהו
      setTimeout(() => {
        // מניעת multiple fallbacks באותו זמן
        if (isGlobalFallbackActive) {
          return;
        }
        
        const target = e.target.closest('button, a');
        if (!target) return;
        
        const timeSinceClick = Date.now() - lastInteractionRef.current;
        
        // אם עברו יותר מ-3 שניות בלי שהלחיצה עשתה משהו
        // (הגדלתי מ-2 ל-3 כדי לתת יותר זמן לפעולות אסינכרוניות)
        if (timeSinceClick > 3000) {
          isGlobalFallbackActive = true;
          console.warn('🚨 Global fallback: click seems unresponsive, checking if needed');
          
          // בדיקה נוספת אם באמת צריך fallback
          // (אולי העמוד השתנה בינתיים)
          const currentUrl = window.location.href;
          
          setTimeout(() => {
            // אם ה-URL השתנה, אז הניווט עבד
            if (window.location.href !== currentUrl) {
              console.info('✅ Navigation worked, no fallback needed');
              isGlobalFallbackActive = false;
              return;
            }
            
            // ניסיון fallback רק אם באמת נחוץ
            if (target.tagName === 'A' && target.href && target.href !== window.location.href) {
              console.warn('🔧 Applying navigation fallback');
              window.location.href = target.href;
            } else if (target.onclick && typeof target.onclick === 'function') {
              try {
                console.warn('🔧 Applying click fallback');
                target.onclick.call(target, e);
              } catch (error) {
                console.error('Global fallback failed:', error);
              }
            }
            
            // Reset flag after fallback attempt
            setTimeout(() => {
              isGlobalFallbackActive = false;
            }, 1000);
          }, 500);
        }
      }, 3500);
    }, { passive: false, capture: true });
  }, []);

  // התחלת הניטור
  useEffect(() => {
    // בדיקה ראשונית
    setTimeout(() => {
      checkButtonHealth();
      repairButtons();
      addGlobalClickFallback();
    }, 1000);

    // בדיקה תקופתית
    healthCheckIntervalRef.current = setInterval(() => {
      checkButtonHealth();
      repairButtons();
    }, 30000); // כל 30 שניות

    // ניקוי
    return () => {
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
    };
  }, [checkButtonHealth, repairButtons, addGlobalClickFallback]);

  // פונקציה ידנית לבדיקת בריאות
  const manualHealthCheck = useCallback(() => {
    const result = checkButtonHealth();
    repairButtons();
    return result;
  }, [checkButtonHealth, repairButtons]);

  return {
    manualHealthCheck,
    lastInteraction: lastInteractionRef.current
  };
}; 